'use client';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createRangeSelectionFromDom, $getSelection, $isRangeSelection, $setSelection, PASTE_COMMAND, } from 'lexical';
import { useEffect } from 'react';
/** Set on the editable root while type-to-focus is enabled. */
const TYPE_TO_FOCUS_ATTRIBUTE = 'data-type-to-focus';
const EDITABLE_SELECTOR = 'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"]';
// These semantics cover native, Aether/Radix, and legacy portalled overlays.
// Query the document because focus may still be on the page behind an opening overlay.
// A dialog that sets aria-modal="false" (for example, the cookie banner) leaves the page usable.
const OVERLAY_SELECTOR = 'dialog[open], [role="dialog"]:not([aria-modal="false"]), [role="alertdialog"]:not([aria-modal="false"]), [aria-modal="true"], [role="menu"], [role="listbox"], [popover], [data-type="portal"], [data-radix-popper-content-wrapper] > [data-state="open"]';
/**
 * `includeTransparent` counts `opacity: 0` as visible. Overlays need it: they start
 * their enter animation transparent but already own the keyboard. The Composer root
 * does not: a transparent root would receive text the user cannot see.
 */
function isVisible(element, includeTransparent = false) {
    // Cheapest checks first. Attribute lookups need no style or layout.
    if (!element.isConnected || element.closest('[hidden], [inert], [aria-hidden="true"]') !== null) {
        return false;
    }
    if (!hasVisibleStyles(element, includeTransparent))
        return false;
    // Size last: it is the only check that can need layout, and it still rejects an
    // empty anchor such as a closed typeahead menu.
    const { width, height } = element.getBoundingClientRect();
    return width > 0 && height > 0;
}
function hasVisibleStyles(element, includeTransparent) {
    // One native call covers display, visibility, opacity, and content-visibility: hidden
    // on every ancestor.
    if (typeof element.checkVisibility === 'function') {
        return element.checkVisibility({
            visibilityProperty: true,
            opacityProperty: !includeTransparent,
        });
    }
    // Engines without checkVisibility (older Safari, jsdom) walk computed styles instead.
    for (let ancestor = element; ancestor !== null; ancestor = ancestor.parentElement) {
        const style = element.ownerDocument.defaultView?.getComputedStyle(ancestor);
        if (style?.display === 'none' ||
            style?.visibility === 'hidden' ||
            style?.visibility === 'collapse' ||
            (!includeTransparent && style?.opacity === '0') ||
            style?.contentVisibility === 'hidden') {
            return false;
        }
    }
    return true;
}
function isInputMethodKey(event, editor) {
    return (event.isComposing ||
        // Some IMEs report their first key before compositionstart/isComposing.
        event.keyCode === 229 ||
        editor.isComposing());
}
function isEditableTarget(target) {
    return target instanceof Element && target.closest(EDITABLE_SELECTOR) !== null;
}
function hasEditableFocus(event, ownerDocument) {
    // The composed path also catches editable targets inside shadow roots, where
    // `activeElement` only reports the shadow host.
    return (isEditableTarget(ownerDocument.activeElement) || event.composedPath().some(isEditableTarget));
}
/**
 * True for a key that inserts one visible character, excluding Space. Shift and
 * AltGraph still produce printable characters; Ctrl, Alt, and Meta chords are
 * shortcuts.
 */
function isPrintableKey(event) {
    const isAltGraph = event.getModifierState('AltGraph');
    const hasShortcutModifier = event.metaKey || ((event.ctrlKey || event.altKey) && !isAltGraph);
    // `Array.from` counts code points, so astral characters such as emoji count as one.
    const isSingleCharacter = Array.from(event.key).length === 1;
    return (!hasShortcutModifier &&
        isSingleCharacter &&
        event.key !== ' ' &&
        !/\p{Control}/u.test(event.key));
}
function isOverlayOpen(ownerDocument) {
    const overlays = ownerDocument.querySelectorAll(OVERLAY_SELECTOR);
    // Count transparent overlays: an opening overlay owns keyboard input during its fade-in.
    // A legacy portal wrapper has no box of its own; its fixed-position children do.
    return Array.from(overlays).some((overlay) => isVisible(overlay, true) ||
        Array.from(overlay.children).some((child) => child instanceof HTMLElement && isVisible(child, true)));
}
/**
 * Selectionchange can lag a native caret move. Prefer that DOM selection while it
 * still belongs to this editor, before focus can replace it.
 */
function readSelectionToRestore(editor, root) {
    return editor.read(() => {
        const previous = $getSelection();
        const domSelection = root.ownerDocument.getSelection();
        if ((previous === null || $isRangeSelection(previous)) &&
            domSelection !== null &&
            root.contains(domSelection.anchorNode) &&
            root.contains(domSelection.focusNode)) {
            const current = $createRangeSelectionFromDom(domSelection, editor);
            if (current !== null) {
                if ($isRangeSelection(previous)) {
                    current.format = previous.format;
                    current.style = previous.style;
                }
                return current;
            }
        }
        return previous?.clone() ?? null;
    });
}
/**
 * Moves focus into the Composer, at its previous selection, when typing or a paste
 * starts outside it. Printable non-space keys keep native insertion; a paste is
 * replayed on the editor. Nothing moves while another text field has focus or an
 * overlay is open, during input-method composition, or into a read-only or hidden
 * editor. Disabling it leaves the draft intact.
 */
export function TypeToFocusPlugin({ enabled }) {
    const [editor] = useLexicalComposerContext();
    useEffect(() => {
        if (!enabled)
            return;
        /**
         * Moves focus into the editor at its previous selection. Typing or pasting into another
         * text field, an open overlay, or a read-only or hidden editor stays where it is.
         * Another Composer that already took the input also counts as a text field.
         *
         * @returns Whether the editor now has focus.
         */
        const takeFocus = (event, root) => {
            const ownerDocument = root.ownerDocument;
            if (hasEditableFocus(event, ownerDocument) || isOverlayOpen(ownerDocument))
                return false;
            // A transparent root counts as hidden, so text never lands where the user can't see it.
            if (!editor.isEditable() || !isVisible(root))
                return false;
            const selection = readSelectionToRestore(editor, root);
            root.focus({ preventScroll: true });
            if (ownerDocument.activeElement !== root)
                return false;
            // Restore Lexical's selection before the input lands. Its normal focus behavior
            // supplies the default when no selection exists.
            editor.update(() => {
                $setSelection(selection);
                editor.focus();
            }, { discrete: true });
            return true;
        };
        const handleKeyDown = (event) => {
            // Never redirect an IME composition. Cheap checks run first; document queries
            // and layout reads run last.
            if (isInputMethodKey(event, editor))
                return;
            const root = editor.getRootElement();
            if (root === null || root.contains(root.ownerDocument.activeElement))
                return;
            // A page handler that canceled the key keeps it.
            if (event.defaultPrevented || !isPrintableKey(event))
                return;
            // Printable keys keep their native insertion into the newly focused editor.
            takeFocus(event, root);
        };
        // Outside an editable element, a paste has no target, so the browser inserts
        // nothing. Replay it on the editor, where Lexical and Composer paste handlers,
        // such as file attachments, see it as a normal paste.
        const handlePaste = (event) => {
            if (event.defaultPrevented)
                return;
            const root = editor.getRootElement();
            if (root === null || root.contains(root.ownerDocument.activeElement))
                return;
            if (!takeFocus(event, root))
                return;
            const replay = new ClipboardEvent('paste', {
                bubbles: true,
                cancelable: true,
                clipboardData: event.clipboardData,
            });
            if (replay.clipboardData !== null) {
                root.dispatchEvent(replay);
            }
            else {
                // Engines that drop `clipboardData` from the constructor still paste the text
                // through Lexical.
                editor.dispatchCommand(PASTE_COMMAND, event);
            }
            event.preventDefault();
        };
        // Window bubble listeners run after document handlers, regardless of mount
        // order, so a page handler's preventDefault wins. With several enabled Composers, the first to register takes the
        // input; the others then see focus in a text field and yield.
        let windowWithListener = null;
        let markedRoot = null;
        const removeListeners = () => {
            windowWithListener?.removeEventListener('keydown', handleKeyDown);
            windowWithListener?.removeEventListener('paste', handlePaste);
            markedRoot?.removeAttribute(TYPE_TO_FOCUS_ATTRIBUTE);
        };
        const unregisterRoot = editor.registerRootListener((root) => {
            removeListeners();
            windowWithListener = root?.ownerDocument.defaultView ?? null;
            windowWithListener?.addEventListener('keydown', handleKeyDown);
            windowWithListener?.addEventListener('paste', handlePaste);
            // Lets page-level shortcuts that would cancel typing step aside.
            markedRoot = root;
            markedRoot?.setAttribute(TYPE_TO_FOCUS_ATTRIBUTE, '');
        });
        return () => {
            unregisterRoot();
            removeListeners();
        };
    }, [editor, enabled]);
    return null;
}
//# sourceMappingURL=TypeToFocusPlugin.js.map